import type { PricedItem, PriceTier } from '@/app/types/item';

/**
 * Rate lookup and line maths.
 *
 * Rates are NOT resolved here. The backend's POST /pricing/quote resolves every
 * pricing rule (quantity tiers, site prices, the party's pricing mode) and
 * returns the item's whole ladder; this module only finds the band a quantity
 * falls in. That keeps the app correct for any pricing mode the backend adds
 * (backend/project_docs/dynamic-pricing/CLIENT_PRICING_CONTRACT.md).
 *
 * Rates are locked in the UI — the manager cannot correct a wrong number.
 */

const round = (value: number, decimalPlaces: number): number => {
    // ROUND_HALF_UP, matching the server's Decimal quantize.
    const factor = 10 ** decimalPlaces;
    return Math.round((value + Number.EPSILON) * factor) / factor;
};

export interface ResolvedRate {
    rate: number;
    /** What a standard customer pays; sent with the line. Null when unknown. */
    standardRate: number | null;
    /** False when the item has no price at this quantity. */
    priced: boolean;
    /** Merchant tier label to surface next to the rate, e.g. "BUY 300+ pc @". */
    tierLabel: string | null;
}

const tierAt = (item: PricedItem, quantity: number): PriceTier | undefined => {
    const qty = Math.max(quantity, 0);
    return item.tiers.find(
        (tier) => tier.minQty <= qty && (tier.maxQty == null || qty <= tier.maxQty)
    );
};

/** The rate the server charges for this item at this quantity. */
export const resolveRate = (item: PricedItem, quantity: number): ResolvedRate => {
    const tier = tierAt(item, quantity);
    if (!tier) return { rate: 0, standardRate: null, priced: false, tierLabel: null };
    return {
        rate: tier.rate,
        standardRate: tier.standardRate,
        priced: true,
        tierLabel: tier.label,
    };
};

export interface TierStep {
    /** Quantity at which this rate starts applying. */
    minQty: number;
    rate: number;
    /** Merchant label for the tier, e.g. "BUY 10+ kg @". Often absent. */
    label: string | null;
    /** True when the current quantity already earns this rate. */
    reached: boolean;
}

/**
 * The item's price ladder: every quantity threshold that buys a better rate.
 *
 * Steps that do not beat the rate already in hand are dropped, which keeps the
 * ladder strictly descending. Display only; the numbers are the server's.
 */
export const priceLadder = (item: PricedItem, quantity: number): TierStep[] => {
    // Rows with nothing selected are previewed at qty 1, so compare against that.
    const effectiveQty = quantity > 0 ? quantity : 1;
    const steps: TierStep[] = [];
    let bestSoFar = resolveRate(item, 0).rate;

    for (const tier of item.tiers) {
        if (tier.minQty <= 0) continue;
        if (bestSoFar > 0 && tier.rate >= bestSoFar) continue;
        steps.push({
            minQty: tier.minQty,
            rate: tier.rate,
            label: tier.label,
            reached: effectiveQty >= tier.minQty,
        });
        bestSoFar = tier.rate;
    }

    return steps;
};

export interface LineTotals {
    taxableAmount: number;
    taxAmount: number;
    totalAmount: number;
}

/**
 * Line maths, mirroring calculate_item_tax_amounts. No discount is sent in this flow.
 *
 * Almost every item in this catalogue is tax-inclusive, so the common case is simply
 * total = quantity x rate, with the tax backed out rather than added on top.
 *
 * These figures are for display only — the server recomputes them on create.
 */
export const lineTotals = (
    quantity: number,
    rate: number,
    taxPercent: number,
    isTaxInclusive: boolean
): LineTotals => {
    const gross = quantity * rate;

    if (!taxPercent) {
        return {
            taxableAmount: round(gross, 2),
            taxAmount: 0,
            totalAmount: round(gross, 2),
        };
    }

    if (isTaxInclusive) {
        const taxAmount = (gross * taxPercent) / (100 + taxPercent);
        return {
            taxableAmount: round(gross - taxAmount, 2),
            taxAmount: round(taxAmount, 2),
            totalAmount: round(gross, 2),
        };
    }

    const taxAmount = (gross * taxPercent) / 100;
    return {
        taxableAmount: round(gross, 2),
        taxAmount: round(taxAmount, 2),
        totalAmount: round(gross + taxAmount, 2),
    };
};

/** ₹1,23,456.78 — Indian digit grouping, matching the rest of the app. */
export const formatInr = (value: number, maximumFractionDigits: number = 2): string =>
    `₹${value.toLocaleString('en-IN', {
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits,
    })}`;

/** Quantity display without trailing ".00" but keeping real fractions. */
export const formatQuantity = (value: number): string =>
    Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));
